import { useQuery } from '@apollo/react-hooks';
import React, { Dispatch, Fragment, FunctionComponent, ReactNode, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../../components/Loader';
import {
  CheckLoginDocument,
  CheckLoginQuery,
  CheckLoginQueryVariables,
  GetUserDocument,
  GetUserQuery,
  GetUserQueryVariables,
  User as IUser,
} from '../../generated/graphql';
import { LoginValues } from './types';

function isAfterLogin(arg: any): arg is CheckLoginQuery {
  return arg.login !== undefined;
}

interface Props extends LoginValues {
  onReset: () => void;
  onError: () => void;
  children: ReactNode;
  authToken: string | null;
  setAuthToken: (token: string) => void;
  setMfaRequired: Dispatch<SetStateAction<boolean>>;
  setMfaQR: Dispatch<SetStateAction<string>>;
  user: IUser | null;
  setUser: (user: IUser | null) => void;
}

const LoginQuery: FunctionComponent<Props> = ({
  username,
  password,
  onError,
  children,
  authToken,
  setAuthToken,
  setMfaRequired,
  setMfaQR,
  user,
  setUser,
}) => {
  const { t } = useTranslation('Authorization');

  const hasUser = Boolean(user && authToken);
  const shouldRunLogin = Boolean(username && password && !hasUser);

  const queryDoc = shouldRunLogin ? CheckLoginDocument : GetUserDocument;
  const variables: any = shouldRunLogin ? { username, password } : { userId: user ? user.id : '' };

  const { loading, error, data } = useQuery<
    CheckLoginQuery | GetUserQuery,
    CheckLoginQueryVariables | GetUserQueryVariables
  >(queryDoc, {
    variables,
    fetchPolicy: 'network-only',
    skip: !shouldRunLogin && !hasUser,
  });

  if (loading) {
    return <Loader>{t('validate')}</Loader>;
  }

  if (error) {
    onError();
    return null;
  }

  if (isAfterLogin(data)) {
    const { login } = data || {};

    switch (login?.__typename) {
      case 'LoginMfaVerify':
        setMfaRequired(true);
        break;

      case 'LoginMfaRequired':
        setMfaRequired(true);
        setMfaQR(login.qrCodeUrl);
        break;

      case 'LoginSuccess':
        setAuthToken(login.token);
        setUser(login.user as IUser);
        break;

      default:
        onError();
    }
  }

  return <Fragment>{children}</Fragment>;
};

export default LoginQuery;
